/**
 * Stable codes for API errors that reach a seller or an end customer.
 *
 * The API attaches a code (and, where useful, params) to its error response;
 * the web maps the code to a translated message under `errors.<code>` and
 * never shows the English exception text. `Design.publishFailReason` stores
 * one of these codes for publish failures. Adding a code means adding its
 * translation to every locale file — a test enforces that.
 */
export const API_ERROR_CODES = [
  // Access and lookups
  'access_denied',
  'email_domain_reserved',
  'design_not_found',
  'version_not_found',
  'product_not_found',
  'product_not_in_design',
  'position_not_configured',
  'asset_not_found',
  'job_not_found',
  // Direct Sale
  'quote_not_found',
  'quote_already_redeemed',
  'quote_already_linked',
  'quote_design_mismatch',
  'quote_design_missing',
  'quote_quantity_out_of_range',
  'quote_size_unknown',
  'product_not_sellable',
  'product_uncalibrated',
  'position_uncalibrated',
  'design_without_placement',
  'position_not_in_product',
  'position_unavailable',
  'decoration_method_unavailable',
  'image_without_asset',
  'foreign_asset',
  'design_object_not_allowed',
  'font_not_allowed',
  'nothing_to_print',
  'print_file_failed',
  'direct_sale_commit_limit',
  // Print data fetch by Phoenix (GET /printfiles?quoteId&sku)
  'printfiles_unconfigured',
  'printfiles_forbidden',
  'printfile_quote_invalid',
  'printfile_quote_not_found',
  'printfile_sku_mismatch',
  'printfile_missing',
  'printfile_pending',
  'printfile_failed',
  'printfile_stopped',
  'printfiles_rate_limited',
  // Pricing
  'selling_price_below_base_cost',
  'design_product_unpriced',
  'design_product_negative_profit',
  // Files
  'file_missing',
  'file_type_invalid',
  'file_too_large',
  'embroidery_source_invalid',
  // AI suggestion
  'ai_not_configured',
  'ai_no_content',
  'ai_empty_response',
  'ai_image_unavailable',
  'ai_failed',
  // Review and publishing
  'review_conflict',
  'publish_queue_failed',
  'account_not_linked',
  'no_completed_mockups',
  'mockups_incomplete',
  'mockups_pending',
  'shop_unreachable',
  'publish_rejected',
  'publish_failed',
  'publish_job_lost',
  'item_failed',
  // Publish channels
  'publish_enqueue_failed',
  'publish_channel_gone',
  'publish_channel_not_configured',
  'publish_no_supplier_ids',
  'publish_in_progress',
  'publish_design_changed',
  // Bulk Creation
  'bulk_reference_unavailable',
  'bulk_invalid_products',
  'bulk_no_active_products',
  'bulk_row_empty',
  'bulk_too_many_jobs',
  'bulk_job_running',
  'bulk_queue_failed',
  'too_many_items',
  // Admin impersonation
  'impersonation_store_unavailable',
  'impersonation_invalid_password',
  'impersonation_target_forbidden',
  'impersonation_target_not_found',
  'impersonation_code_invalid',
  // Publish handover
  'no_handover_target',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** Values a message may interpolate; the API resolves names itself. */
export type ApiErrorParams = Record<
  string,
  string | number | boolean | null | undefined
>;

export function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return (
    typeof value === 'string' &&
    (API_ERROR_CODES as readonly string[]).includes(value)
  );
}
